import { types, flow, getEnv, type Instance } from 'mobx-state-tree';
import type { IStoreEnv } from './env';

// A UserIdentity (see ARCHITECTURE.md)
const UserIdentity = types.model('UserIdentity', {
  provider: types.enumeration(['kuleuven', 'google', 'facebook']),
  key: types.optional(types.maybeNull(types.string), null),
});

// A User (see ARCHITECTURE.md)
const User = types
  .model('User', {
    id: types.string,
    firstname: types.string,
    lastname: types.string,
    email: types.string,
    type: types.enumeration('AccountType', ['STUDENT', 'PERSONAL', 'COMPANY', 'OTHER']),
    affiliation: types.maybeNull(types.string),
    identities: types.array(UserIdentity),
  })
  .views((self) => ({
    get kuleuvenId() {
      const kulProvider = self.identities.find((i) => i.provider === 'kuleuven');
      if (kulProvider) {
        return kulProvider.key;
      } else {
        return null;
      }
    },
    get name() {
      return `${self.firstname} ${self.lastname}`;
    },
  }));

/**
 * Store the currently logged in user along with 2 booleans: whether or not that
 * user is initialized and whether or not we are currently registering (needed
 * to show a spinner in the UI).
 */
const Auth = types
  .model('Auth', {
    user: types.maybeNull(User),
    initialized: false,
    registering: false,
  })
  .views((self) => ({
    get authenticated() {
      return self.user !== null;
    },
  }))
  .actions((self) => ({
    /**
     * Initialize the logged in user.
     *
     * Requests 'me' from the api and assigns it to the currently logged in user
     * if the api returns a valid user.
     */
    initialize: flow(function* initialize() {
      try {
        const api = getEnv<IStoreEnv>(self).authApi;
        const user = yield api.me();
        if (user) {
          // MST accepts a User snapshot for the `user` model assignment at
          // runtime; the parsed JSON is untyped, so cast at this boundary.
          self.user = user as unknown as typeof self.user;
        }
      } catch (err) {
        //ILB
      } finally {
        self.initialized = true;
      }
    }),
    /**
     * Register a new user with the given details.
     *
     * If the api returns a valid user, that means the registration succeeded and
     * the user is now logged in, thus we assign it to the currently logged in
     * user.
     */
    register: flow(function* register(
      firstname: string,
      lastname: string,
      email: string,
      type: string,
      affiliation: string,
      token: string
    ) {
      self.registering = true;
      try {
        const api = getEnv<IStoreEnv>(self).authApi;
        const user = yield api.register(firstname, lastname, email, type, affiliation, token);
        if (user) {
          self.user = user as unknown as typeof self.user;
        }
      } catch (error) {
        // ILB
      }
      self.registering = false;
    }),
    /**
     * Logout the user.
     *
     * This is not called directly but by a 'logout' action defined on the
     * top-level store because the 'projects' and 'ui' stores also need to be
     * emptied on logout.
     */
    logout: flow(function* logout() {
      try {
        const api = getEnv<IStoreEnv>(self).authApi;
        yield api.logout();
        self.user = null;
      } catch (e) {
        // ILB
      }
    }),
    /**
     * Update the details for the currently logged in user.
     */
    update: flow(function* update(updates: unknown) {
      try {
        const api = getEnv<IStoreEnv>(self).authApi;
        const user = yield api.update(updates);
        self.user = user as unknown as typeof self.user;
      } catch (e) {
        // TODO: error handling?
      }
    }),
  }));

export type IUser = Instance<typeof User>;
export type IAuth = Instance<typeof Auth>;

export default Auth;
